import React from "react";
import { BookOpen, Hammer, Cog, Pipette, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import {
  getReferenceCategories,
  REFERENCE_LIBRARY_VERSION,
  REFERENCE_LAST_UPDATED_ISO,
  REFERENCE_DISCLAIMER,
} from "@/lib/reference";
import ReferenceLibraryBrowser from "@/components/ReferenceLibraryBrowser";

const CATEGORY_ICON = {
  drill_pipe: Pipette,
  hole_size: Hammer,
  pump_output: Cog,
  rig_custom: BookOpen,
};

export default function Reference() {
  const categories = getReferenceCategories();
  return (
    <div className="px-4 py-5 max-w-3xl mx-auto" data-testid="page-reference">
      <div className="flex items-start gap-3 mb-4">
        <div className="shrink-0 mt-1 rounded-md bg-amber-500/15 text-amber-400 p-2">
          <BookOpen className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.10em] font-semibold text-[var(--rs-text-3,#6b7785)]">
            RigSentinel · Reference
          </div>
          <h1 className="text-2xl font-bold text-[var(--rs-text,#e8edf2)] mt-0.5" data-testid="reference-title">
            Reference Data Library
          </h1>
          <div className="text-[11px] mt-1 text-[var(--rs-text-dim,#9aa6b2)]">
            Library {REFERENCE_LIBRARY_VERSION} · Updated {REFERENCE_LAST_UPDATED_ISO}
          </div>
        </div>
      </div>

      <div
        data-testid="reference-disclaimer"
        className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2.5 mb-5"
      >
        <ShieldAlert
          className="w-4 h-4 mt-0.5 shrink-0"
          style={{ color: "var(--rs-amber, #f5b400)" }}
          aria-hidden="true"
        />
        <p className="text-xs leading-relaxed text-[var(--rs-text,#e8edf2)]">
          {REFERENCE_DISCLAIMER}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {categories.map((cat) => {
          const Icon = CATEGORY_ICON[cat.id] || BookOpen;
          const isPilot = cat.mode === "pilot";
          // v2 Reference Libraries Hub mapping. Anything mapped here becomes
          // a navigable card so the user can open the managed CRUD library.
          const HUB_MAP = {
            drill_pipe: "drill_pipe",
            hwdp: "hwdp",
            drill_collar: "drill_collar",
            casing: "casing",
            tubing: "tubing",
            bit: "bit",
            pump: "pump",
            mud_motor: "mud_motor",
            rotary_steerable: "rotary_steerable",
            mwd_tool: "mwd_tool",
            lwd_tool: "lwd_tool",
            stabilizer: "stabilizer",
            crossover: "crossover",
            bop_stack: "bop",
          };
          const hubKey = HUB_MAP[cat.id];
          const cardBody = (
            <div className="flex items-start gap-2.5">
              <div className="shrink-0 mt-0.5 rounded-md bg-amber-500/10 text-amber-400 p-1.5">
                <Icon className="w-4 h-4" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-[var(--rs-text,#e8edf2)]">{cat.label}</div>
                  {isPilot && (
                    <span
                      data-testid={`reference-card-${cat.id}-pilot-badge`}
                      className="text-[9px] uppercase tracking-[0.10em] font-semibold px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-400"
                    >
                      Pilot
                    </span>
                  )}
                  {cat.mode === "future" && (
                    <span className="text-[9px] uppercase tracking-[0.10em] font-semibold px-1.5 py-0.5 rounded border border-[var(--rs-border,#1f2630)] text-[var(--rs-text-dim,#9aa6b2)]">
                      Future phase
                    </span>
                  )}
                  {hubKey && (
                    <span
                      data-testid={`reference-card-${cat.id}-manage-badge`}
                      className="text-[9px] uppercase tracking-[0.10em] font-semibold px-1.5 py-0.5 rounded border"
                      style={{ borderColor: "var(--rs-amber, #f5b400)", color: "var(--rs-amber, #f5b400)" }}
                    >
                      Manage
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-[var(--rs-text-dim,#9aa6b2)] leading-relaxed">
                  {cat.tagline}
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.08em] text-[var(--rs-text-3,#6b7785)]">
                  {cat.items.length} entr{cat.items.length === 1 ? "y" : "ies"}
                </div>
              </div>
            </div>
          );
          const baseClass = "rounded-md border p-3 bg-[var(--rs-panel-2,#161b21)] border-[var(--rs-border,#1f2630)]";
          if (hubKey) {
            return (
              <Link
                key={cat.id}
                to={`/reference-libraries/${hubKey}`}
                data-testid={`reference-card-${cat.id}`}
                className={`${baseClass} block transition-colors hover:border-[var(--rs-amber,#f5b400)]`}
              >
                {cardBody}
              </Link>
            );
          }
          return (
            <div key={cat.id} data-testid={`reference-card-${cat.id}`} className={baseClass}>
              {cardBody}
            </div>
          );
        })}
      </div>

      <div className="mt-5 text-xs text-[var(--rs-text-dim,#9aa6b2)] leading-relaxed">
        Reference items are surfaced inside calculators via a “Pick from library” button next to eligible geometry / pump-output fields. The pilot integration is wired to the{" "}
        <Link to="/calc/balanced_plug_support" className="underline text-[var(--rs-amber,#f5b400)]" data-testid="reference-pilot-link-bp">
          Cement / Mud Balanced Plug Support Calculator
        </Link>{" "}
        and the{" "}
        <Link to="/calc/annular_capacity" className="underline text-[var(--rs-amber,#f5b400)]" data-testid="reference-pilot-link-ac">
          Annular Capacity calculator
        </Link>.
      </div>

      <ReferenceLibraryBrowser />
    </div>
  );
}
